import { Controller } from "@hotwired/stimulus"
import { extractHeadings } from "../components/blocknote/utils/headingExtractor"

// TOC settings controller for admin page form
// Manages heading list display and exclusion toggling for Table of Contents configuration
export default class extends Controller {
  static targets = ["headingList", "emptyState", "hiddenField", "levelToggles"]
  static values = { editorId: String }

  connect() {
    this.excludedIds = this.parseExcludedIds()
    // Store all headings for level toggle functionality
    this.allHeadings = []
    // Delay initial refresh to allow BlockNote editor to initialize
    setTimeout(() => this.refresh(), 500)
    this.startPolling()
  }

  disconnect() {
    this.stopPolling()
  }

  // Parse the stored JSON array of excluded heading IDs
  parseExcludedIds() {
    try {
      const value = this.hiddenFieldTarget.value
      return new Set(JSON.parse(value || "[]"))
    } catch {
      return new Set()
    }
  }

  // Start 30-second interval polling to refresh heading list
  startPolling() {
    this.pollInterval = setInterval(() => this.refresh(), 30000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
  }

  // Refresh heading list from BlockNote editor content
  refresh() {
    const documentContent = this.getEditorContent()
    if (!documentContent || !Array.isArray(documentContent)) {
      this.allHeadings = []
      this.renderHeadingList([])
      return
    }

    // Extract all headings H2-H6 (never H1)
    this.allHeadings = extractHeadings(documentContent, {
      includedLevels: [2, 3, 4, 5, 6],
      hiddenHeadings: []
    })

    this.renderHeadingList(this.allHeadings)
    this.updateLevelToggleStates()
  }

  // Get the BlockNote editor content from the hidden form field
  getEditorContent() {
    // Find the hidden content field that stores the BlockNote document JSON
    // It has data-blocks-text-editor-target="content"
    const contentField = document.querySelector('[data-blocks-text-editor-target="content"]')
    if (!contentField || !contentField.value) {
      return null
    }

    try {
      return JSON.parse(contentField.value)
    } catch (e) {
      console.warn("toc-settings: Failed to parse editor content", e)
      return null
    }
  }

  // Render the checkbox list of headings with indentation
  renderHeadingList(headings) {
    if (headings.length === 0) {
      this.headingListTarget.innerHTML = ""
      this.emptyStateTarget.classList.remove("hidden")
      return
    }

    this.emptyStateTarget.classList.add("hidden")

    const html = headings.map(heading => {
      const isExcluded = this.excludedIds.has(heading.blockId)
      const indent = heading.nestingLevel * 16

      return `
        <label class="flex items-center gap-2 py-1" style="padding-left: ${indent}px" data-heading-level="${heading.level}">
          <input type="checkbox"
                 class="rounded border-neutral-300 text-indigo-600"
                 data-block-id="${heading.blockId}"
                 data-level="${heading.level}"
                 data-action="change->toc-settings#toggleHeading"
                 ${isExcluded ? "" : "checked"}>
          <span class="text-sm text-neutral-700 truncate">${this.escapeHtml(heading.text)}</span>
          <span class="text-xs text-neutral-400">H${heading.level}</span>
        </label>
      `
    }).join("")

    this.headingListTarget.innerHTML = html
  }

  // Toggle a heading's exclusion state
  toggleHeading(event) {
    const blockId = event.target.dataset.blockId
    const isChecked = event.target.checked

    if (isChecked) {
      this.excludedIds.delete(blockId)
    } else {
      this.excludedIds.add(blockId)
    }

    this.updateHiddenField()
    this.updateLevelToggleStates()
  }

  // Toggle all headings of a specific level
  toggleLevel(event) {
    const level = parseInt(event.target.dataset.level, 10)
    const isChecked = event.target.checked

    // Find all headings of this level
    const headingsOfLevel = this.allHeadings.filter(h => h.level === level)

    // Update exclusion set
    headingsOfLevel.forEach(heading => {
      if (isChecked) {
        this.excludedIds.delete(heading.blockId)
      } else {
        this.excludedIds.add(heading.blockId)
      }
    })

    // Update individual checkboxes in the list
    const checkboxes = this.headingListTarget.querySelectorAll(`input[data-level="${level}"]`)
    checkboxes.forEach(checkbox => {
      checkbox.checked = isChecked
    })

    this.updateHiddenField()
  }

  // Update the level toggle checkbox states based on current heading states
  updateLevelToggleStates() {
    if (!this.hasLevelTogglesTarget) return

    // For each level (2-6), check if all headings of that level are included
    for (let level = 2; level <= 6; level++) {
      const headingsOfLevel = this.allHeadings.filter(h => h.level === level)
      const levelToggle = this.levelTogglesTarget.querySelector(`input[data-level="${level}"]`)

      if (!levelToggle) continue

      if (headingsOfLevel.length === 0) {
        // No headings of this level - keep checked but could disable
        levelToggle.checked = true
        levelToggle.indeterminate = false
      } else {
        const includedCount = headingsOfLevel.filter(h => !this.excludedIds.has(h.blockId)).length

        if (includedCount === headingsOfLevel.length) {
          // All included
          levelToggle.checked = true
          levelToggle.indeterminate = false
        } else if (includedCount === 0) {
          // None included
          levelToggle.checked = false
          levelToggle.indeterminate = false
        } else {
          // Some included (indeterminate state)
          levelToggle.checked = false
          levelToggle.indeterminate = true
        }
      }
    }
  }

  // Update the hidden field with the current exclusion set as JSON
  updateHiddenField() {
    this.hiddenFieldTarget.value = JSON.stringify([...this.excludedIds])
  }

  // Escape HTML entities to prevent XSS
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
